import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useChatInputStore, useFileStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import { filesApi } from '@/lib/api/files';
import { formatFileSize } from '@/constants/utils/files';
import { mediaBaseName } from '@/constants/utils/video_project';

const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|aiff?)$/i;
const EMPTY_FILES = [];
const audioId = item => String(item?.id || item?._id || '');
const audioUrl = item => item?.cdnPath || item?.url || item?.src || '';
const audioKey = item => audioId(item) || item?.path || audioUrl(item) || mediaBaseName(item);
const isAudio = item => {
  if (!item) return false;
  const mime = String(item.mimeType || item.contentType || item.type || '').toLowerCase();
  return item.fileType === 'audio' || mime.startsWith('audio/')
    || (item.fileType !== 'video' && !mime.startsWith('video/') && AUDIO_EXT.test(mediaBaseName(item)));
};
const sameAudio = (left, right) => (audioId(left) && audioId(left) === audioId(right))
  || (left.path && left.path === right.path)
  || (audioUrl(left) && audioUrl(left) === audioUrl(right))
  || (mediaBaseName(left) && mediaBaseName(left).toLowerCase() === mediaBaseName(right).toLowerCase());

export function collectVideoReferenceAudio({ artifacts = [], files = [], file } = {}) {
  const items = [];
  for (const candidate of [...(artifacts || []), ...(files || []), file].filter(isAudio)) {
    const item = { ...candidate, name: candidate.name || mediaBaseName(candidate) || 'Audio reference' };
    const previous = items.findIndex(entry => sameAudio(entry, item));
    if (previous < 0) items.push(item);
    else items[previous] = { ...items[previous], ...item };
  }
  return items;
}

function AudioReference({ item, taskId, status, colors, onAttach, readOnly }) {
  const directUrl = audioUrl(item);
  const [source, setSource] = useState(directUrl);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const name = item.name || mediaBaseName(item) || 'Audio reference';
  useEffect(() => {
    let cancelled = false;
    setSource(directUrl); setError('');
    if (directUrl) return undefined;
    (async () => {
      try {
        const url = item.path && taskId
          ? await coworkerApi.getArtifactDownloadUrl(taskId, item.path)
          : audioId(item) ? audioUrl(await filesApi.fetchFile(audioId(item))) : '';
        if (!url) throw new Error('Audio is not available yet.');
        if (!cancelled) setSource(url);
      } catch (err) { if (!cancelled) setError(err?.message || 'Could not load this audio.'); }
    })();
    return () => { cancelled = true; };
  }, [directUrl, item.path, item.id, item._id, taskId, attempt]);
  const NativeAudioPreview = Platform.OS === 'macos'
    ? require('./VideoStudioAudioPreview.macos').default : null;
  return <View style={[styles.card, { borderColor: colors.border_color_secondary }]}>
    <View style={styles.row}>
      <Icon name="file-audio" size={16} color={colors.text_color_secondary} />
      <View style={styles.copy}>
        <Text size="xs" weight="semibold" numberOfLines={1}>{name}</Text>
        <Text size="xxs" color="secondary">{status}{item.size ? ` · ${formatFileSize(item.size)}` : ''}</Text>
      </View>
    </View>
    {source && Platform.OS === 'web' ? (
      <audio key={`${source}:${attempt}`} controls preload="metadata" src={source} aria-label={`Preview ${name}`}
        onError={() => setError('This audio could not play. Try again or use a browser-supported audio file.')}
        style={{ display: 'block', width: '100%', minWidth: 0, height: 36 }} />
    ) : source && NativeAudioPreview ? (
      <NativeAudioPreview key={`${source}:${attempt}`} url={source} file={item} />
    ) : !error ? <Text size="xxs" color="secondary">Loading audio…</Text> : null}
    {error ? <View style={styles.copy}>
      <Text size="xxs" accessibilityRole="alert" color="secondary">{error}</Text>
      <Pressable accessibilityRole="button" accessibilityLabel={`Retry ${name}`} onPress={() => setAttempt(value => value + 1)}>
        <Text size="xs" weight="medium">Try again</Text>
      </Pressable>
    </View> : null}
    {onAttach ? <Pressable accessibilityRole="button" accessibilityLabel={`Add ${name} to chat`} disabled={readOnly} onPress={onAttach} style={[styles.button, { borderColor: colors.border_color_secondary, opacity: readOnly ? 0.5 : 1 }]}>
      <Text size="xs">Add to chat</Text>
    </Pressable> : null}
  </View>;
}

export default function VideoProjectReferenceAudio({ items = EMPTY_FILES, taskId, conversationId, colors, readOnly = false }) {
  const composerFiles = useChatInputStore(state => state.inputStates?.[conversationId]?.files || EMPTY_FILES);
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const scopeRef = useRef('');
  const scope = `${taskId || ''}:${conversationId || ''}`;
  scopeRef.current = scope;
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => { setUploads([]); setBusy(false); setError(''); }, [scope]);
  const pending = useMemo(() => collectVideoReferenceAudio({ files: [...uploads, ...composerFiles] })
    .filter(item => !items.some(saved => sameAudio(saved, item))), [uploads, composerFiles, items]);

  const attach = item => {
    if (readOnlyRef.current) return;
    const composer = useChatInputStore.getState();
    if (!conversationId || composer.currentContextId !== conversationId) throw new Error('Open this project’s chat before attaching reference audio.');
    if (!audioId(item)) throw new Error('The upload did not return a usable file. Please try again.');
    composer.addFilesToContext(conversationId, [{ ...item, id: audioId(item) }]);
    const prompt = `Use the attached audio ${item.name || mediaBaseName(item)} as a reference for this video. This adds a reference only; do not generate clips or stitch the final video until I explicitly approve.`;
    const currentText = composer.inputStates?.[conversationId]?.text || '';
    if (!currentText.includes(prompt)) composer.setText(currentText.trim() ? `${currentText}\n\n${prompt}` : prompt);
    composer.requestInputFocus(conversationId);
  };
  const uploadFile = async file => {
    if (!file || busy || readOnlyRef.current) return;
    setError('');
    if (!isAudio({ ...file, name: file.name, type: file.type })) { setError('Choose an audio file such as MP3, WAV, or M4A.'); return; }
    if (file.size > 50 * 1024 * 1024) { setError('Choose an audio file smaller than 50 MB.'); return; }
    if (!conversationId || useChatInputStore.getState().currentContextId !== conversationId) { setError('Open this project’s chat before uploading reference audio.'); return; }
    const startedIn = scope;
    setBusy(true);
    try {
      const uploaded = await useFileStore.getState().actions.uploadFile({ file, name: file.name });
      if (!aliveRef.current || scopeRef.current !== startedIn) return;
      const item = { ...uploaded, name: uploaded?.name || file.name, fileType: 'audio' };
      if (!audioId(item)) throw new Error('The upload did not return a usable file. Please try again.');
      setUploads(current => [...current.filter(entry => !sameAudio(entry, item)), item]);
      attach(item);
    } catch (err) {
      if (aliveRef.current && scopeRef.current === startedIn) setError(err?.message || 'Could not upload this audio. Please try again.');
    } finally { if (aliveRef.current && scopeRef.current === startedIn) setBusy(false); }
  };
  const upload = event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    return uploadFile(file);
  };
  const chooseNativeAudio = async () => {
    if (busy || readOnlyRef.current) return;
    const startedIn = scope;
    try {
      const { getDocumentAsync } = require('expo-document-picker');
      const result = await getDocumentAsync({ type: 'audio/*', multiple: false, copyToCacheDirectory: true });
      if (result.canceled || !aliveRef.current || scopeRef.current !== startedIn || readOnlyRef.current) return;
      const asset = result.assets?.[0];
      if (asset) await uploadFile({ uri: asset.uri, name: asset.name, size: asset.size, type: asset.mimeType });
    } catch (err) {
      if (aliveRef.current && scopeRef.current === startedIn) setError(err?.message || 'Could not choose reference audio.');
    }
  };
  return <View style={styles.library}>
    <View style={styles.copy}>
      <Text size="s" weight="semibold">Reference audio</Text>
      <Text size="xs" color="secondary">Voice, music, and sound samples help Work match how your video should sound.</Text>
      <Text size="xxs" color="secondary">{readOnly ? 'Reference uploads are currently locked. You can still listen to saved audio.' : 'Uploads are attached to this chat. Press Send to make them available to Work.'}</Text>
    </View>
    {['web', 'macos'].includes(Platform.OS) ? <>
      {Platform.OS === 'web' && <input ref={inputRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.opus,.flac,.aif,.aiff" aria-label="Choose reference audio" disabled={readOnly} onChange={upload} style={{ display: 'none' }} />}
      <Pressable accessibilityRole="button" accessibilityLabel="Upload reference audio" disabled={readOnly || busy || !conversationId}
        onPress={() => { if (!readOnlyRef.current) return Platform.OS === 'macos' ? chooseNativeAudio() : inputRef.current?.click(); }} style={[styles.button, { borderColor: colors.border_color_secondary, opacity: readOnly || busy || !conversationId ? 0.5 : 1 }]}>
        <Icon name="arrow-up-from-bracket" size={12} color={colors.text_color_secondary} />
        <Text size="xs" weight="medium">{busy ? 'Uploading audio…' : 'Upload reference audio'}</Text>
      </Pressable>
    </> : null}
    {error ? <Text size="xs" accessibilityRole="alert" color="secondary">{error}</Text> : null}
    {!items.length && !pending.length ? <Text size="xs" color="secondary">{readOnly ? 'No reference audio.' : 'No reference audio yet. Upload a voice, music, or sound sample.'}</Text> : null}
    {items.map(item => <AudioReference key={`saved:${audioKey(item)}`} item={item} taskId={taskId} colors={colors} status="Saved to this project" />)}
    {pending.map(item => <AudioReference key={`pending:${audioKey(item)}`} item={item} taskId={taskId} colors={colors} readOnly={readOnly}
      status={composerFiles.some(file => sameAudio(file, item)) ? 'Attached to chat · not sent' : 'Uploaded · not attached'}
      onAttach={composerFiles.some(file => sameAudio(file, item)) ? null : () => { try { attach(item); } catch (err) { setError(err.message); } }} />)}
  </View>;
}

const styles = StyleSheet.create({
  library: { gap: 12, padding: 4, minWidth: 0 },
  copy: { flex: 1, minWidth: 0, gap: 4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  card: { gap: 10, padding: 10, borderWidth: 1, borderRadius: 10, minWidth: 0 },
  button: { alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 10, minHeight: 36, borderWidth: 1, borderRadius: 8 },
});
